import { useCallback, useEffect, useRef } from 'react';
import type { MouseEvent, RefObject } from 'react';

/** How long a card keeps its transition names if the navigation never happens (DESIGN_SYSTEM §4.6). */
const CLEAR_AFTER_MS = 1200;

export const SHARED_IMAGE_NAME = 'product-image';
export const SHARED_TITLE_NAME = 'product-title';

export interface SharedElement<I extends HTMLElement, T extends HTMLElement> {
  imageRef: RefObject<I | null>;
  titleRef: RefObject<T | null>;
  /** `onClick` of every link that opens the product page. */
  activate: (event: MouseEvent) => void;
}

/**
 * Shared-element transition from a card to its product page. A `view-transition-name` must be
 * unique on the page, so it is given to a card's image and title only at the moment that card is
 * activated (and taken away again a moment later, in case the click never turned into a navigation —
 * a new-tab click, a cancelled route). Browsers without View Transitions, and modified clicks, are
 * left alone. The product page gives its own gallery image the same name.
 */
export function useSharedElement<
  I extends HTMLElement = HTMLElement,
  T extends HTMLElement = HTMLElement,
>(): SharedElement<I, T> {
  const imageRef = useRef<I | null>(null);
  const titleRef = useRef<T | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const clear = useCallback(() => {
    imageRef.current?.style.removeProperty('view-transition-name');
    titleRef.current?.style.removeProperty('view-transition-name');
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      clear();
    },
    [clear],
  );

  const activate = useCallback(
    (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        !('startViewTransition' in document)
      ) {
        return;
      }
      imageRef.current?.style.setProperty('view-transition-name', SHARED_IMAGE_NAME);
      titleRef.current?.style.setProperty('view-transition-name', SHARED_TITLE_NAME);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(clear, CLEAR_AFTER_MS);
    },
    [clear],
  );

  return { imageRef, titleRef, activate };
}
